import { useRecordingList, useRecordingPlayer, useRecordingVideo, useRecordingReplay, useRecordingLanes, useRecordingClock, useRecentRecordingScopes, useRecordingFullscreen, useRecordingCrossLinks, type RecordingReplayHandle } from "./react.tsx";
export { OPEN_FLOW_RECORDING_EVENT, type OpenFlowRecordingDetail } from "./events.ts";
import {
  ArrowLeft,
  ArrowsClockwiseIcon,
  CaretLeft,
  CaretRight,
  Check,
  Copy,
  CornersIn,
  CornersOut,
  FilmStrip,
  Pause,
  Play,
  Trash,
  WarningCircle
} from "@phosphor-icons/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  type BrowserObservationEvent
} from "@workerstation/shared";
import {
  FLOW_RECORDING_RUNTIME_VERSION,
  type FlowRecordingMarkerKind,
  type FlowRecordingMode,
  type FlowRecordingState
} from "@workerstation/shared";
import {
  buildFrameTimeline,
  buildPlaybackTimeMap,
  formatPlayerClock,
  frameAtTime,
  isLaneRowHighlighted,
  markerPipFraction,
  stepPlaybackClock,
  type FlowRecordingFrameRef
} from "@workerstation/shared";
import type {
  FlowRecordingListResult,
  FlowRecordingSegmentSummary,
  FlowRecordingSummary
} from "@workerstation/shared";
import {
  formatResourceBytes,
  ResourceTreeAction
} from "../ResourceExplorerTree.tsx";

/**
 * Cross-linking contract: `BrowserObservabilityExplorer` session rows emit
 * this window CustomEvent because the resource-explorer tab registry lives in
 * `client.tsx`, out of reach of sibling explorers. Two listeners react to it:
 * `client.tsx` switches the active tab to "flow-recordings" (it owns that
 * state), and this explorer applies the session filter carried in the detail.
 */
interface FlowRecordingsExplorerProps {
  workspaceId: string | null;
  connectionName: string;
  collapsed: boolean;
  headerLeading?: ReactNode;
  refreshToken?: number;
  onCollapsedChange: (collapsed: boolean) => void;
  /** Deep-link: pre-select a recording (and the scope it lives under) on mount
   *  so a shared URL opens straight into the player. */
  initialRecordingId?: string;
  initialScope?: string;
  /** Hand the selected recording to the workbench, which opens it as an editor
   *  tab. Without it the explorer falls back to playing inline. */
  onOpenRecording?: (
    recordingId: string,
    scope: string,
    flowName: string
  ) => void;
}

const EMPTY_LISTING: FlowRecordingListResult = {
  recordings: [],
  stats: { total: 0, byState: {}, byMode: {}, totalBytes: 0 },
  policy: { retentionMs: 0, workspaceRecordingLimit: 0, workspaceByteLimit: 0 },
  generatedAt: 0
};

const PLAYBACK_SPEEDS = [0.5, 1, 2, 4] as const;
const STATE_BADGE_CLASS: Record<FlowRecordingState, string> = {
  recording: "bg-sky-500/15 text-sky-500",
  complete: "bg-emerald-500/15 text-emerald-500",
  interrupted: "bg-amber-500/15 text-amber-600",
  expired: "bg-kumo-elevated text-kumo-subtle"
};

function StateBadge({ state }: { state: FlowRecordingState }) {
  return (
    <span
      className={`shrink-0 rounded px-1 text-[8px] font-semibold uppercase ${STATE_BADGE_CLASS[state] ?? "bg-kumo-elevated text-kumo-subtle"}`}
    >
      {state}
    </span>
  );
}

function ModeBadge({ mode }: { mode: FlowRecordingMode }) {
  return (
    <span className="shrink-0 rounded bg-violet-500/15 px-1 text-[8px] font-semibold uppercase text-violet-500">
      {mode}
    </span>
  );
}

const CORRELATION_CHIP_FIELDS = [
  "changeId",
  "taskId",
  "runId",
  "parentRunId",
  "attemptId",
  "worktreeId",
  "treeId",
  "inputTreeId",
  "outputTreeId",
  "candidateId",
  "checkId",
  "reviewId",
  "namespaceId",
  "fence",
  "cacheResult",
  "eventSequence",
  "targetRef",
  "phase"
] as const;

function correlationChips(
  recording: FlowRecordingSummary
): Array<{ field: string; value: string }> {
  const chips: Array<{ field: string; value: string }> = [];
  const record = recording as unknown as Record<string, unknown>;
  for (const field of CORRELATION_CHIP_FIELDS) {
    const value = record[field];
    if (value === undefined || value === null || value === "") continue;
    chips.push({ field, value: String(value) });
  }
  return chips;
}

function recordingDurationMs(recording: FlowRecordingSummary): number | undefined {
  if (typeof recording.durationMs === "number") return recording.durationMs;
  const end = recording.endedAt ?? recording.heartbeatAt;
  if (typeof end === "number" && end >= recording.startedAt) {
    return end - recording.startedAt;
  }
  return undefined;
}

export function FlowRecordingsExplorer({
  workspaceId,
  connectionName,
  collapsed,
  headerLeading,
  refreshToken,
  onCollapsedChange,
  initialRecordingId,
  initialScope,
  onOpenRecording
}: FlowRecordingsExplorerProps) {
  const [search, setSearch] = useState("");
  const [modeFilter, setModeFilter] = useState<"" | FlowRecordingMode>("");
  const [stateFilter, setStateFilter] = useState<"" | FlowRecordingState>("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [live, setLive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef(selectedId);
  // When a recording from a different scope is opened (e.g. a hosted-test
  // recording under its ephemeral test-e2e anchor), the explorer focuses that
  // scope so both the listing and the player resolve it. Cleared on a real
  // workspace switch.
  const [scopeOverride, setScopeOverride] = useState<string | null>(null);
  // Scopes recent test runs recorded into. Hosted-test recordings live under an
  // ephemeral anchor, so without these the tab is empty unless the user arrives
  // by clicking a recording in the Test tab.
  const [scopeVersion, setScopeVersion] = useState(0);
  const runScopes = useRecentRecordingScopes(workspaceId, scopeVersion);
  const effectiveWorkspaceId = scopeOverride ?? workspaceId;
  const workspaceRef = useRef(effectiveWorkspaceId);
  workspaceRef.current = effectiveWorkspaceId;
  selectedIdRef.current = selectedId;

  useEffect(() => {
    setSearch("");
    setModeFilter("");
    setStateFilter("");
    setSessionFilter("");
    setSelectedId(null);
    setScopeOverride(null);
    setScopeVersion(value => value + 1);
  }, [workspaceId]);

  // Deep-link intake: a shared "watch" URL pre-selects a recording (under its
  // own scope) so this explorer opens straight into the player on mount. Runs
  // once; subsequent scope/selection is user-driven.
  useEffect(() => {
    if (!initialRecordingId) return;
    if (initialScope) setScopeOverride(initialScope.toLowerCase());
    setSelectedId(initialRecordingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { snapshot: list, refresh } = useRecordingList({ workspaceId: effectiveWorkspaceId, collapsed, live, refreshToken: refreshToken ?? 0, flowName: search, mode: modeFilter, state: stateFilter, sessionId: sessionFilter });
  const listing = list.listing ?? EMPTY_LISTING;
  const { loading, error } = list;

  // Cross-link intake; see OPEN_FLOW_RECORDING_EVENT above.
  useRecordingCrossLinks(workspaceId, detail => {
    setScopeOverride(detail.workspaceId);
    if (detail.sessionId !== null) setSessionFilter(detail.sessionId);
    setSelectedId(detail.recordingId);
    if (workspaceId) setScopeVersion(value => value + 1);
  });

  const inProgressCount = useMemo(
    () =>
      listing.recordings.filter((recording) => recording.state === "recording")
        .length,
    [listing.recordings]
  );

  return (
    <section className="flex h-full min-h-0 w-full flex-col border-t border-kumo-line">
      <div className="flex h-7 shrink-0 items-center border-b border-kumo-line/60 px-1">
        {headerLeading ?? (
          <button
            type="button"
            aria-expanded={!collapsed}
            onClick={() => onCollapsedChange(!collapsed)}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 bg-transparent px-1 text-left text-[10px] font-semibold uppercase tracking-wide text-kumo-default"
          >
            <FilmStrip size={12} /> Flow recordings
          </button>
        )}
        <button
          type="button"
          aria-pressed={live}
          title={live ? "Disable live refresh" : "Enable live refresh"}
          onClick={() => setLive((value) => !value)}
          className={`mr-0.5 h-4 rounded px-1 text-[8px] font-semibold uppercase ${
            live
              ? "bg-emerald-500/15 text-emerald-500"
              : "bg-kumo-elevated text-kumo-subtle"
          }`}
        >
          live
        </button>
        <ResourceTreeAction
          label="Refresh flow recordings"
          onClick={() => void refresh()}
          disabled={!effectiveWorkspaceId || loading}
        >
          <ArrowsClockwiseIcon
            size={13}
            className={loading ? "animate-spin" : ""}
          />
        </ResourceTreeAction>
      </div>

      {!collapsed ? (
        effectiveWorkspaceId && selectedId && !onOpenRecording ? (
          <RecordingPlayerPane
            workspaceId={effectiveWorkspaceId}
            recordingId={selectedId}
            live={live}
            onBack={() => setSelectedId(null)}
            onDeleted={() => {
              if (
                workspaceRef.current !== effectiveWorkspaceId ||
                selectedIdRef.current !== selectedId
              ) {
                return;
              }
              setSelectedId(null);
              void refresh();
            }}
          />
        ) : (
          <>
            <div className="grid shrink-0 grid-cols-2 gap-1 border-b border-kumo-line/60 p-1">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search flow name…"
                aria-label="Search flow recordings by flow name"
                className="col-span-2 h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1.5 text-[10px] text-kumo-default outline-none focus:border-kumo-accent"
              />
              <select
                value={modeFilter}
                onChange={(event) =>
                  setModeFilter(event.target.value as "" | FlowRecordingMode)
                }
                aria-label="Filter flow recordings by mode"
                className="h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1 text-[10px] text-kumo-default"
              >
                <option value="">all modes</option>
                <option value="video">video</option>
                <option value="dom">dom</option>
                <option value="both">both</option>
              </select>
              <select
                value={stateFilter}
                onChange={(event) =>
                  setStateFilter(event.target.value as "" | FlowRecordingState)
                }
                aria-label="Filter flow recordings by state"
                className="h-6 min-w-0 rounded border border-kumo-line bg-kumo-base px-1 text-[10px] text-kumo-default"
              >
                <option value="">all states</option>
                <option value="recording">recording</option>
                <option value="complete">complete</option>
                <option value="interrupted">interrupted</option>
                <option value="expired">expired</option>
              </select>
              {sessionFilter ? (
                <div className="col-span-2 flex min-w-0 items-center gap-1 rounded border border-kumo-line bg-kumo-elevated/40 px-1.5 py-0.5 text-[9px] text-kumo-subtle">
                  <span className="truncate">session: {sessionFilter}</span>
                  <button
                    type="button"
                    aria-label="Clear session filter"
                    onClick={() => setSessionFilter("")}
                    className="ml-auto shrink-0 cursor-pointer rounded bg-transparent px-1 text-kumo-subtle hover:text-kumo-default"
                  >
                    ×
                  </button>
                </div>
              ) : null}
            </div>
            <div className="grid shrink-0 grid-cols-3 gap-px border-b border-kumo-line/60 bg-kumo-line/60 text-center text-[9px]">
              <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
                <strong className="block text-xs text-kumo-default">
                  {listing.stats.total}
                </strong>
                recordings
              </div>
              <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
                <strong
                  className={
                    inProgressCount
                      ? "block text-xs text-sky-500"
                      : "block text-xs text-kumo-default"
                  }
                >
                  {inProgressCount}
                </strong>
                in progress
              </div>
              <div className="bg-kumo-base px-1 py-1 text-kumo-subtle">
                <strong className="block text-xs text-kumo-default">
                  {formatResourceBytes(listing.stats.totalBytes)}
                </strong>
                stored
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-auto py-1">
              {effectiveWorkspaceId ? (
                <ul aria-label={`Flow recordings for ${connectionName || "workerspace"}`}>
                  {listing.recordings.map((recording) => {
                    const chips = correlationChips(recording);
                    const duration = recordingDurationMs(recording);
                    return (
                      <li key={recording.recordingId}>
                        <button
                          type="button"
                          aria-label={`Open flow recording ${recording.flowName}`}
                          onClick={() => {
                            setSelectedId(recording.recordingId);
                            onOpenRecording?.(
                              recording.recordingId,
                              effectiveWorkspaceId ?? "",
                              recording.flowName
                            );
                          }}
                          className="flex w-full cursor-pointer flex-col gap-0.5 bg-transparent px-2 py-1 text-left text-xs hover:bg-kumo-elevated/60"
                        >
                          <span className="flex min-w-0 items-center gap-1">
                            <FilmStrip size={13} className="shrink-0" />
                            <span className="truncate font-medium text-kumo-default">
                              {recording.flowName}
                            </span>
                            <ModeBadge mode={recording.mode} />
                            <StateBadge state={recording.state} />
                          </span>
                          <span className="truncate text-[9px] text-kumo-subtle">
                            {new Date(recording.startedAt).toLocaleString()} ·{" "}
                            {duration !== undefined
                              ? formatPlayerClock(duration)
                              : "—"}{" "}
                            · {formatResourceBytes(recording.totalBytes)} ·{" "}
                            {recording.frameCount} frames ·{" "}
                            {recording.eventCount} events
                          </span>
                          {chips.length ? (
                            <span className="flex flex-wrap gap-0.5">
                              {chips.map((chip) => (
                                <span
                                  key={chip.field}
                                  title={`${chip.field}: ${chip.value}`}
                                  className="max-w-32 truncate rounded bg-kumo-elevated px-1 text-[8px] text-kumo-subtle"
                                >
                                  {chip.field}:{chip.value}
                                </span>
                              ))}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="p-2 text-[11px] text-kumo-subtle">
                  Open a workerspace to inspect flow recordings.
                </p>
              )}
              {scopeOverride ? (
                <div className="mx-2 mt-2 flex items-center gap-2 rounded border border-kumo-line bg-kumo-tint/40 p-2 text-[11px] leading-4 text-kumo-subtle">
                  <span className="min-w-0 flex-1">
                    Showing a test run's scope{" "}
                    <code className="font-mono text-kumo-default">
                      {scopeOverride}
                    </code>
                    . These recordings are kept per run, not with the
                    workerspace.
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setScopeOverride(null);
                      setSelectedId(null);
                    }}
                    className="shrink-0 rounded border border-kumo-line px-1.5 py-0.5 text-kumo-default hover:bg-kumo-fill"
                  >
                    Back to workerspace
                  </button>
                </div>
              ) : null}
              {!listing.recordings.length && !loading && workspaceId ? (
                <div className="mx-2 mt-2 rounded border border-dashed border-kumo-line p-2 text-[11px] leading-4 text-kumo-subtle">
                  {runScopes.length > 0 && !scopeOverride ? (
                    <>
                      <p>
                        Nothing recorded under this workerspace. Recent test
                        runs recorded under their own scope — open one:
                      </p>
                      <div className="mt-1.5 flex flex-col gap-1">
                        {runScopes.map((scope) => (
                          <button
                            key={scope.scopeId}
                            type="button"
                            onClick={() => {
                              setScopeOverride(scope.scopeId);
                              setSelectedId(null);
                            }}
                            className="truncate rounded border border-kumo-line bg-kumo-base px-1.5 py-1 text-left font-mono text-[10px] text-kumo-default hover:bg-kumo-fill"
                            title={`List recordings under ${scope.scopeId}`}
                          >
                            {scope.scopeId}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      No flow recordings yet. Run a recorded E2E flow
                      (FLOW_RECORDING=video|dom|both) or enable recording on a
                      Browser binding; pixel and DOM replays will appear here.
                      Test-run recordings are kept per run and appear here once
                      a run reports one.
                    </>
                  )}
                </div>
              ) : null}
              {error ? (
                <div className="m-2 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
                  {error}
                </div>
              ) : null}
            </div>
          </>
        )
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Player pane
// ---------------------------------------------------------------------------

const CAPTURE_QUALITY_MARKER_KINDS: FlowRecordingMarkerKind[] = [
  "dropped-frames",
  "quality-degraded",
  "truncated"
];

/**
 * Standalone, iframe-embeddable player for a single flow recording. Renders the
 * session-replay/video player full-viewport with no IDE chrome, a resizable
 * video area (drag the grip), and a fullscreen toggle.
 */
export function EmbeddedRecordingPlayer({
  workspaceId,
  recordingId,
  initialShot
}: {
  workspaceId: string;
  recordingId: string;
  /** Deep-link (?shot=<markerSeq>): open in Screenshots mode at that marker. */
  initialShot?: number;
}) {
  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-kumo-base text-kumo-default">
      <RecordingPlayerPane
        workspaceId={workspaceId}
        recordingId={recordingId}
        live={false}
        embedded
        initialShot={initialShot}
        onBack={() => {}}
        onDeleted={() => {}}
      />
    </div>
  );
}

export function RecordingPlayerPane({
  workspaceId,
  recordingId,
  live,
  onBack,
  onDeleted,
  embedded = false,
  initialShot
}: {
  workspaceId: string;
  recordingId: string;
  live: boolean;
  onBack: () => void;
  onDeleted: () => void;
  /** Standalone iframe-embed mode: hides list/destructive chrome and adds a
   *  resizable video area plus a fullscreen toggle. */
  embedded?: boolean;
  /** Deep-link: open Screenshots mode at the marker with this markerSeq. */
  initialShot?: number;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const videoBoxRef = useRef<HTMLDivElement>(null);
  const isFullscreen = useRecordingFullscreen();
  const toggleFullscreen = () => {
    fullscreen(rootRef.current);
  };
  const { snapshot: player, dispatch, fullscreen } = useRecordingPlayer({ workspaceId, recordingId, live }, onDeleted);
  const { manifest, exportStatus, exportReason, deleting, copiedMarker } = player;
  const [playheadState, setPlayheadState] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [skipInactive, setSkipInactive] = useState(true);
  const [activeMode, setActiveMode] = useState<
    "video" | "dom" | "screenshots" | null
  >(null);
  const [shotIndex, setShotIndex] = useState(0);
  const scope = JSON.stringify([workspaceId, recordingId]);
  const domPlayerRef = useRef<RecordingReplayHandle | null>(null);
  const domBaseTsRef = useRef(0);
  useEffect(() => { setPlayheadState(null); setPlaying(false); setActiveMode(null); }, [scope]);

  const recording = manifest?.recording;
  const segments = useMemo(() => manifest?.segments ?? [], [manifest]);
  const markers = useMemo(() => manifest?.markers ?? [], [manifest]);
  const timeline = useMemo(() => buildFrameTimeline(segments), [segments]);

  const bounds = useMemo(() => {
    if (!recording) return { startMs: 0, endMs: 1 };
    let startMs = recording.startedAt;
    let endMs = recording.endedAt ?? recording.heartbeatAt ?? recording.startedAt;
    for (const segment of segments) {
      if (segment.firstTs > 0) startMs = Math.min(startMs, segment.firstTs);
      endMs = Math.max(endMs, segment.lastTs);
    }
    for (const marker of markers) {
      endMs = Math.max(endMs, marker.timestampMs);
    }
    return { startMs, endMs: Math.max(endMs, startMs + 1) };
  }, [markers, recording, segments]);

  const playbackMap = useMemo(() => {
    const activity = [
      ...timeline.map((frame) => frame.timestampMs),
      ...markers.map((marker) => marker.timestampMs)
    ];
    return buildPlaybackTimeMap(activity, bounds.startMs, bounds.endMs);
  }, [bounds.endMs, bounds.startMs, markers, timeline]);

  const playhead = Math.min(
    bounds.endMs,
    Math.max(bounds.startMs, playheadState ?? bounds.startMs)
  );

  const hasVideo = segments.some((segment) => segment.kind === "video");
  const hasDom = segments.some((segment) => segment.kind === "dom");
  // Screenshots are marker-anchored views of the video frames: each recorded
  // marker (step/assertion/navigation/error) is a slide.
  const hasScreenshots = hasVideo && markers.length > 0;
  const mode = activeMode ?? (hasVideo ? "video" : hasDom ? "dom" : null);
  const clampedShot = Math.min(Math.max(0, shotIndex), Math.max(0, markers.length - 1));
  const currentShot = markers[clampedShot];

  // Deep-link intake (?shot=<markerSeq>): open Screenshots mode at that marker.
  useEffect(() => {
    if (initialShot === undefined || !markers.length) return;
    const idx = markers.findIndex((m) => m.markerSeq === initialShot);
    if (idx < 0) return;
    setShotIndex(idx);
    setActiveMode("screenshots");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once markers load
  }, [markers.length]);

  const seek = useCallback(
    (timestampMs: number) => {
      const clamped = Math.min(
        bounds.endMs,
        Math.max(bounds.startMs, timestampMs)
      );
      setPlayheadState(clamped);
      if (mode === "dom" && domPlayerRef.current) {
        try {
          domPlayerRef.current.goto(Math.max(0, clamped - domBaseTsRef.current));
        } catch {
          // The replay player may not be ready yet; the state seek stands.
        }
      }
    },
    [bounds.endMs, bounds.startMs, mode]
  );

  const clockKey = useMemo(() => JSON.stringify([scope, bounds.startMs, bounds.endMs, mode, speed, skipInactive, playbackMap]), [scope, bounds.startMs, bounds.endMs, mode, speed, skipInactive, playbackMap]);
  const clock = useRecordingClock({ key: clockKey, active: playing && mode === "video" }, delta => {
    setPlayheadState(previous => Math.min(stepPlaybackClock(previous ?? bounds.startMs, delta, speed, skipInactive ? playbackMap : undefined), bounds.endMs));
  });
  const paneError = player.error ?? clock.error;

  useEffect(() => {
    if (playing && mode === "video" && playhead >= bounds.endMs) {
      setPlaying(false);
    }
  }, [bounds.endMs, mode, playhead, playing]);

  const togglePlaying = () => {
    if (mode === "dom" && domPlayerRef.current) {
      try {
        if (playing) domPlayerRef.current.pause();
        else domPlayerRef.current.play();
      } catch {
        // fall through to state toggle
      }
      setPlaying((value) => !value);
      return;
    }
    if (!playing && playhead >= bounds.endMs) setPlayheadState(bounds.startMs);
    setPlaying((value) => !value);
  };

  const changeSpeed = (value: number) => {
    setSpeed(value);
    if (mode === "dom" && domPlayerRef.current) {
      try {
        domPlayerRef.current.setSpeed(value);
      } catch {
        // controller not ready; state speed applies to the video clock
      }
    }
  };

  const switchMode = (next: "video" | "dom" | "screenshots") => {
    if (next === mode) return;
    if (mode === "dom" && domPlayerRef.current) {
      try {
        domPlayerRef.current.pause();
      } catch {
        // ignore
      }
    }
    setPlaying(false);
    // Entering Screenshots: jump the slide to the marker nearest the playhead.
    if (next === "screenshots" && markers.length) {
      let nearest = 0;
      let best = Infinity;
      markers.forEach((marker, index) => {
        const distance = Math.abs(marker.timestampMs - playhead);
        if (distance < best) {
          best = distance;
          nearest = index;
        }
      });
      setShotIndex(nearest);
    }
    // playheadState carries the timeline position across the toggle; the
    // DOM player seeds itself from it on mount, the video player renders it.
    setActiveMode(next);
  };

  const copyShotRef = (markerSeq: number) => dispatch({ _tag: "Copy", markerSeq });
  const requestExport = () => dispatch({ _tag: "Export" });
  const deleteRecording = () => dispatch({ _tag: "Delete" });

  if (!recording) {
    return (
      <div className="min-h-0 flex-1 overflow-auto">
        <div className="flex items-center gap-1 border-b border-kumo-line/60 px-1 py-1">
          <ResourceTreeAction label="Back to flow recordings list" onClick={onBack}>
            <ArrowLeft size={13} />
          </ResourceTreeAction>
          <span className="text-[10px] text-kumo-subtle">
            {paneError ?? "Loading flow recording…"}
          </span>
        </div>
      </div>
    );
  }

  const effectiveExportStatus = exportStatus ?? recording.exportStatus ?? null;
  const qualityMarkers = markers.filter((marker) =>
    CAPTURE_QUALITY_MARKER_KINDS.includes(marker.kind)
  );
  const runtimeMismatch =
    hasDom &&
    recording.runtimeVersion !== undefined &&
    recording.runtimeVersion !== FLOW_RECORDING_RUNTIME_VERSION;

  return (
    <div
      ref={rootRef}
      className={
        embedded
          ? "flex min-h-0 flex-1 flex-col overflow-hidden bg-kumo-base"
          : "flex min-h-0 flex-1 flex-col overflow-auto bg-kumo-base"
      }
    >
      <div className="flex shrink-0 items-center gap-1 border-b border-kumo-line/60 px-1 py-1">
        {embedded ? (
          <FilmStrip size={13} className="mx-1 shrink-0 text-kumo-subtle" />
        ) : (
          <ResourceTreeAction
            label="Back to flow recordings list"
            onClick={onBack}
          >
            <ArrowLeft size={13} />
          </ResourceTreeAction>
        )}
        <span className="min-w-0 truncate text-xs font-medium text-kumo-default">
          {recording.flowName}
        </span>
        <ModeBadge mode={recording.mode} />
        <StateBadge state={recording.state} />
        <span className="ml-auto" />
        <button
          type="button"
          onClick={() => void requestExport()}
          disabled={!hasVideo}
          title={
            hasVideo
              ? "Export this recording's pixels to MP4"
              : "MP4 export requires video segments"
          }
          className="h-5 shrink-0 cursor-pointer rounded border border-kumo-line bg-transparent px-1.5 text-[9px] text-kumo-default hover:bg-kumo-fill disabled:cursor-default disabled:opacity-40"
        >
          Export MP4
        </button>
        <ResourceTreeAction
          label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <CornersIn size={13} /> : <CornersOut size={13} />}
        </ResourceTreeAction>
        {embedded ? null : (
          <ResourceTreeAction
            label="Delete flow recording"
            disabled={deleting}
            onClick={() => void deleteRecording()}
          >
            <Trash size={13} />
          </ResourceTreeAction>
        )}
      </div>

      {paneError ? (
        <div className="m-1 rounded bg-red-500/10 p-2 text-[11px] text-status-error">
          {paneError}
        </div>
      ) : null}

      {recording.state === "interrupted" || qualityMarkers.length ? (
        <div className="m-1 flex items-start gap-1 rounded bg-amber-500/10 px-2 py-1 text-[10px] leading-4 text-amber-600">
          <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
          <span>
            {recording.state === "interrupted"
              ? "This recording was interrupted; it plays up to the last flushed segment. "
              : ""}
            {qualityMarkers
              .map(
                (marker) =>
                  `${marker.kind} at ${formatPlayerClock(marker.timestampMs - bounds.startMs)}`
              )
              .join(" · ")}
          </span>
        </div>
      ) : null}

      {effectiveExportStatus ? (
        <div className="mx-1 flex items-center gap-1 rounded bg-kumo-elevated/40 px-2 py-1 text-[9px] text-kumo-subtle">
          <span>
            export: {effectiveExportStatus}
            {exportReason ? ` — ${exportReason}` : ""}
          </span>
          {effectiveExportStatus === "complete" ? (
            <a
              href={player.downloadUrl ?? undefined}
              download={`${recordingId}.mp4`}
              className="ml-auto text-kumo-accent underline"
            >
              Download MP4
            </a>
          ) : null}
        </div>
      ) : null}

      {(
        [
          ["video", hasVideo],
          ["dom", hasDom],
          ["screenshots", hasScreenshots]
        ] as const
      ).filter(([, show]) => show).length > 1 ? (
        <div
          role="group"
          aria-label="Playback mode"
          className="mx-1 mt-1 flex w-fit shrink-0 items-center gap-px overflow-hidden rounded border border-kumo-line"
        >
          {(
            [
              ["video", hasVideo],
              ["dom", hasDom],
              ["screenshots", hasScreenshots]
            ] as const
          )
            .filter(([, show]) => show)
            .map(([id]) => (
              <button
                key={id}
                type="button"
                aria-pressed={mode === id}
                onClick={() => switchMode(id)}
                className={`h-5 cursor-pointer px-2 text-[9px] font-semibold uppercase ${
                  mode === id
                    ? "bg-kumo-fill text-kumo-accent"
                    : "bg-transparent text-kumo-subtle hover:text-kumo-default"
                }`}
              >
                {id}
              </button>
            ))}
        </div>
      ) : null}

      {runtimeMismatch ? (
        <p className="mx-1 mt-1 rounded bg-amber-500/10 px-2 py-1 text-[9px] text-amber-600">
          This recording was captured with recorder runtime v
          {recording.runtimeVersion}; the player bundles v
          {FLOW_RECORDING_RUNTIME_VERSION}. Replay may misrender.
        </p>
      ) : null}

      <div
        ref={videoBoxRef}
        className={
          embedded
            ? "relative m-1 min-h-0 flex-1 overflow-hidden rounded"
            : "m-1 shrink-0"
        }
      >
        {mode === "video" ? (
          <FlowRecordingVideoCanvas
            workspaceId={workspaceId}
            recordingId={recordingId}
            timeline={timeline}
            playheadMs={playhead}
            fill={embedded}
          />
        ) : null}
        {mode === "dom" ? (
          <FlowRecordingDomPlayer
            workspaceId={workspaceId}
            recordingId={recordingId}
            segments={segments}
            hasVideo={hasVideo}
            fill={embedded}
            initialPlayheadMs={playhead}
            playerRef={domPlayerRef}
            baseTsRef={domBaseTsRef}
            onPlayhead={(absoluteMs) => setPlayheadState(absoluteMs)}
          />
        ) : null}
        {mode === "screenshots" && currentShot ? (
          <div
            className={
              embedded
                ? "relative flex h-full min-h-0 w-full flex-col"
                : "relative flex w-full flex-col"
            }
          >
            <div className={embedded ? "relative min-h-0 flex-1" : "relative"}>
              <img
                src={player.imageUrls[String(currentShot.markerSeq)]}
                alt={`Flow recording screenshot: ${currentShot.label}`}
                loading="eager"
                decoding="async"
                draggable={false}
                className={
                  embedded
                    ? "h-full w-full rounded bg-black object-contain"
                    : "max-h-64 w-full rounded bg-black object-contain"
                }
              />
              <button
                type="button"
                aria-label="Previous screenshot"
                disabled={clampedShot <= 0}
                onClick={() => setShotIndex((index) => Math.max(0, index - 1))}
                className="absolute left-1 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:cursor-default disabled:opacity-30"
              >
                <CaretLeft size={16} weight="bold" />
              </button>
              <button
                type="button"
                aria-label="Next screenshot"
                disabled={clampedShot >= markers.length - 1}
                onClick={() =>
                  setShotIndex((index) =>
                    Math.min(markers.length - 1, index + 1)
                  )
                }
                className="absolute right-1 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 disabled:cursor-default disabled:opacity-30"
              >
                <CaretRight size={16} weight="bold" />
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-2 border-t border-kumo-line/60 bg-kumo-base px-2 py-1">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${MARKER_PIP_CLASS[currentShot.kind] ?? "bg-kumo-subtle"}`}
              />
              <span className="text-[8px] font-semibold uppercase tracking-wide text-kumo-subtle">
                {currentShot.kind}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] text-kumo-default">
                {currentShot.label}
              </span>
              <span className="shrink-0 text-[9px] tabular-nums text-kumo-subtle">
                {formatPlayerClock(currentShot.timestampMs - bounds.startMs)}
              </span>
              <span className="shrink-0 text-[9px] tabular-nums text-kumo-subtle">
                {clampedShot + 1} / {markers.length}
              </span>
              <button
                type="button"
                aria-label="Copy screenshot reference"
                title={`Copy shareable ref — ${player.shareUrls[String(currentShot.markerSeq)] ?? ""}`}
                onClick={() => void copyShotRef(currentShot.markerSeq)}
                className="flex shrink-0 cursor-pointer items-center gap-1 rounded border border-kumo-line bg-transparent px-1.5 py-0.5 text-[9px] font-medium text-kumo-default hover:bg-kumo-fill"
              >
                {copiedMarker === currentShot.markerSeq ? (
                  <Check size={11} weight="bold" className="text-status-success" />
                ) : (
                  <Copy size={11} />
                )}
                {copiedMarker === currentShot.markerSeq
                  ? "copied"
                  : "copy ref"}
              </button>
            </div>
          </div>
        ) : null}
        {!mode ? (
          <p className="rounded border border-dashed border-kumo-line p-2 text-[10px] text-kumo-subtle">
            No playable segments have been flushed yet.
          </p>
        ) : null}
      </div>

      {mode === "screenshots" ? null : (
      <div className="flex shrink-0 items-center gap-1 px-1">
        <button
          type="button"
          aria-label={playing ? "Pause playback" : "Play recording"}
          onClick={togglePlaying}
          className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded bg-transparent text-kumo-default hover:bg-kumo-fill"
        >
          {playing ? (
            <Pause size={13} weight="fill" />
          ) : (
            <Play size={13} weight="fill" />
          )}
        </button>
        <select
          aria-label="Playback speed"
          value={speed}
          onChange={(event) => changeSpeed(Number(event.target.value))}
          className="h-5 shrink-0 rounded border border-kumo-line bg-kumo-base px-1 text-[9px] text-kumo-default"
        >
          {PLAYBACK_SPEEDS.map((value) => (
            <option key={value} value={value}>
              {value}×
            </option>
          ))}
        </select>
        {mode === "video" ? (
          <button
            type="button"
            aria-pressed={skipInactive}
            aria-label="Skip inactive periods"
            title="Compress gaps longer than 5s to 500ms"
            onClick={() => setSkipInactive((value) => !value)}
            className={`h-5 shrink-0 rounded px-1 text-[8px] font-semibold uppercase ${
              skipInactive
                ? "bg-kumo-fill text-kumo-accent"
                : "bg-kumo-elevated text-kumo-subtle"
            }`}
          >
            skip idle
          </button>
        ) : null}
        <input
          type="range"
          min={0}
          max={Math.max(1, Math.round(bounds.endMs - bounds.startMs))}
          step={100}
          value={Math.round(playhead - bounds.startMs)}
          onChange={(event) => seek(bounds.startMs + Number(event.target.value))}
          aria-label="Playback position"
          aria-valuetext={`${formatPlayerClock(playhead - bounds.startMs)} of ${formatPlayerClock(bounds.endMs - bounds.startMs)}`}
          className="min-w-0 flex-1"
        />
      </div>
      )}

      <MarkerTimeline
        markers={markers}
        startMs={bounds.startMs}
        endMs={bounds.endMs}
        playheadMs={mode === "screenshots" && currentShot ? currentShot.timestampMs : playhead}
        onSeek={(ts) => {
          if (mode === "screenshots") {
            let nearest = 0;
            let best = Infinity;
            markers.forEach((marker, index) => {
              const distance = Math.abs(marker.timestampMs - ts);
              if (distance < best) {
                best = distance;
                nearest = index;
              }
            });
            setShotIndex(nearest);
          } else {
            seek(ts);
          }
        }}
      />

      <div className="flex shrink-0 items-center justify-between px-1 text-[9px] text-kumo-subtle">
        <span aria-live="off">
          {formatPlayerClock(playhead - bounds.startMs)} /{" "}
          {formatPlayerClock(bounds.endMs - bounds.startMs)}
        </span>
        <span title="Wall clock at the playhead">
          {new Date(playhead).toLocaleTimeString()}
        </span>
      </div>

      <ContextLanes
        workspaceId={workspaceId}
        sessionId={recording.sessionId}
        startMs={bounds.startMs}
        endMs={bounds.endMs}
        playheadMs={playhead}
        onSeek={seek}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Video canvas player
// ---------------------------------------------------------------------------

function FlowRecordingVideoCanvas({
  workspaceId,
  recordingId,
  timeline,
  playheadMs,
  fill = false
}: {
  workspaceId: string;
  recordingId: string;
  timeline: FlowRecordingFrameRef[];
  playheadMs: number;
  /** Fill the parent (resizable) box instead of the default capped height. */
  fill?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const index = frameAtTime(timeline, playheadMs);
  const displayIndex = index < 0 ? (timeline.length ? 0 : -1) : index;
  const { error: frameError } = useRecordingVideo({ workspaceId, recordingId }, timeline, canvasRef, displayIndex);

  if (!timeline.length) {
    return (
      <p className="rounded border border-dashed border-kumo-line p-2 text-[10px] text-kumo-subtle">
        No video frames have been flushed yet.
      </p>
    );
  }

  return (
    <div className={fill ? "h-full" : undefined}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label="Flow recording video frame"
        className={
          fill
            ? "h-full w-full rounded bg-black object-contain"
            : "max-h-64 w-full rounded bg-black object-contain"
        }
      />
      {frameError ? (
        <p className="mt-0.5 text-[9px] text-status-error">{frameError}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DOM (rrweb) player
// ---------------------------------------------------------------------------

function FlowRecordingDomPlayer({
  workspaceId,
  recordingId,
  segments,
  hasVideo,
  fill = false,
  initialPlayheadMs,
  playerRef,
  baseTsRef,
  onPlayhead
}: {
  workspaceId: string;
  recordingId: string;
  segments: readonly FlowRecordingSegmentSummary[];
  hasVideo: boolean;
  /** Fill the parent player surface, matching embedded video mode. */
  fill?: boolean;
  initialPlayheadMs: number;
  playerRef: { current: RecordingReplayHandle | null };
  baseTsRef: { current: number };
  onPlayhead: (absoluteMs: number) => void;
}) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const { snapshot, handle } = useRecordingReplay({ workspaceId, recordingId, segments, hasVideo, initialPlayheadMs }, target, onPlayhead);
  const { status, message } = snapshot;
  useEffect(() => {
    if (status !== "ready" || handle === null) return;
    playerRef.current = handle;
    baseTsRef.current = snapshot.baseMs;
    return () => { if (playerRef.current === handle) playerRef.current = null; };
  }, [status, handle, playerRef, baseTsRef, snapshot.baseMs]);

  return (
    <div className={fill ? "relative h-full min-h-0 w-full" : undefined}>
      <div
        ref={setTarget}
        aria-label="DOM replay player"
        className={
          fill
            ? "relative h-full min-h-0 w-full overflow-hidden rounded border border-kumo-line"
            : "relative h-64 min-h-8 w-full overflow-hidden rounded border border-kumo-line"
        }
      />
      {status === "loading" ? (
        <p
          className={
            fill
              ? "pointer-events-none absolute left-1 top-1 z-10 rounded bg-kumo-base/90 p-1 text-[10px] text-kumo-subtle"
              : "p-1 text-[10px] text-kumo-subtle"
          }
        >
          Loading DOM replay…
        </p>
      ) : null}
      {status === "error" && message ? (
        <p
          className={
            fill
              ? "absolute left-1 right-1 top-1 z-10 rounded bg-kumo-base/90 p-1 text-[10px] text-status-error"
              : "p-1 text-[10px] text-status-error"
          }
        >
          {message}
        </p>
      ) : null}
      {!fill ? (
        <p className="mt-0.5 text-[9px] leading-3 text-kumo-subtle">
          DOM replay reconstructs the page from recorded events; cross-origin
          iframes and non-opted-in canvas regions render as placeholders.
          {hasVideo
            ? " The video (pixels) mode of this recording covers them."
            : " Record video mode to capture those regions as pixels."}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared marker timeline strip
// ---------------------------------------------------------------------------

const MARKER_PIP_CLASS: Record<FlowRecordingMarkerKind, string> = {
  step: "bg-sky-500",
  assertion: "bg-emerald-500",
  error: "bg-red-500",
  navigation: "bg-violet-500",
  "test-start": "bg-sky-400",
  "test-pass": "bg-emerald-500",
  "test-fail": "bg-red-500",
  "dropped-frames": "bg-amber-500",
  "quality-degraded": "bg-amber-500",
  truncated: "bg-amber-600",
  custom: "bg-kumo-subtle"
};

function MarkerTimeline({
  markers,
  startMs,
  endMs,
  playheadMs,
  onSeek
}: {
  markers: ReadonlyArray<{
    markerSeq: number;
    kind: FlowRecordingMarkerKind;
    label: string;
    timestampMs: number;
  }>;
  startMs: number;
  endMs: number;
  playheadMs: number;
  onSeek: (timestampMs: number) => void;
}) {
  const playheadFraction = markerPipFraction(playheadMs, startMs, endMs);
  return (
    <div className="shrink-0 px-1 py-0.5">
      <div
        role="group"
        aria-label="Marker timeline"
        className="relative h-4 rounded bg-kumo-elevated"
      >
        <div
          aria-hidden
          className="absolute top-0 h-full w-px bg-kumo-accent"
          style={{ left: `${(playheadFraction * 100).toFixed(3)}%` }}
        />
        {markers.map((marker) => {
          const relative = formatPlayerClock(marker.timestampMs - startMs);
          const wallClock = new Date(marker.timestampMs).toLocaleTimeString();
          return (
            <button
              key={marker.markerSeq}
              type="button"
              aria-label={`Seek to ${marker.kind} marker: ${marker.label}`}
              title={`${marker.kind} · ${marker.label} · ${relative} (${wallClock})`}
              onClick={() => onSeek(marker.timestampMs)}
              style={{
                left: `${(markerPipFraction(marker.timestampMs, startMs, endMs) * 100).toFixed(3)}%`
              }}
              className={`absolute top-0.5 h-3 w-1 -translate-x-1/2 cursor-pointer rounded-sm ${MARKER_PIP_CLASS[marker.kind] ?? "bg-kumo-subtle"}`}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Console / network context lanes (joined from browser observability)
// ---------------------------------------------------------------------------

const CONSOLE_CATEGORIES = new Set(["runtime", "log", "console"]);

function ContextLanes({
  workspaceId,
  sessionId,
  startMs,
  endMs,
  playheadMs,
  onSeek
}: {
  workspaceId: string;
  sessionId?: string;
  startMs: number;
  endMs: number;
  playheadMs: number;
  onSeek: (timestampMs: number) => void;
}) {
  const { events, error: laneError } = useRecordingLanes({ workspaceId, sessionId, startMs, endMs });

  if (!sessionId) {
    return (
      <p className="m-1 rounded border border-dashed border-kumo-line p-2 text-[10px] leading-4 text-kumo-subtle">
        No browser session is linked to this recording, so console and network
        context is unavailable.
      </p>
    );
  }
  if (laneError) {
    return (
      <p className="m-1 rounded bg-red-500/10 p-2 text-[10px] text-status-error">
        {laneError}
      </p>
    );
  }
  if (events === null) {
    return (
      <p className="m-1 p-1 text-[10px] text-kumo-subtle">
        Loading console and network context…
      </p>
    );
  }
  if (!events.length) {
    return (
      <p className="m-1 rounded border border-dashed border-kumo-line p-2 text-[10px] leading-4 text-kumo-subtle">
        No observability events exist for this recording&apos;s session — they
        were purged or never captured.
      </p>
    );
  }

  const consoleRows = events.filter((event) =>
    CONSOLE_CATEGORIES.has(event.category)
  );
  const networkRows = events.filter((event) => event.category === "network");
  return (
    <div className="min-h-0 shrink-0 px-1 pb-1">
      <ContextLane
        title="Console"
        rows={consoleRows}
        startMs={startMs}
        playheadMs={playheadMs}
        onSeek={onSeek}
        emptyText="No console events for this session."
      />
      <ContextLane
        title="Network"
        rows={networkRows}
        startMs={startMs}
        playheadMs={playheadMs}
        onSeek={onSeek}
        emptyText="No network events for this session."
      />
    </div>
  );
}

function ContextLane({
  title,
  rows,
  startMs,
  playheadMs,
  onSeek,
  emptyText
}: {
  title: string;
  rows: BrowserObservationEvent[];
  startMs: number;
  playheadMs: number;
  onSeek: (timestampMs: number) => void;
  emptyText: string;
}) {
  return (
    <div className="mt-1">
      <p className="px-1 text-[8px] font-semibold uppercase tracking-wide text-kumo-subtle">
        {title}
      </p>
      {rows.length ? (
        <ul
          aria-label={`${title} lane`}
          className="max-h-24 overflow-auto rounded border border-kumo-line/60"
        >
          {rows.map((event) => {
            const highlighted = isLaneRowHighlighted(
              event.timestamp,
              playheadMs
            );
            return (
              <li key={event.id}>
                <button
                  type="button"
                  aria-label={`Seek to ${event.method} at ${formatPlayerClock(event.timestamp - startMs)}`}
                  aria-current={highlighted ? "true" : undefined}
                  title={event.summary}
                  onClick={() => onSeek(event.timestamp)}
                  className={`flex w-full cursor-pointer items-center gap-1 px-1 py-0.5 text-left text-[9px] ${
                    highlighted
                      ? "bg-kumo-accent/15 text-kumo-default"
                      : "bg-transparent text-kumo-subtle hover:bg-kumo-elevated/60"
                  }`}
                >
                  <span className="shrink-0 tabular-nums">
                    {formatPlayerClock(event.timestamp - startMs)}
                  </span>
                  <span
                    className={`truncate ${event.level === "error" ? "text-red-500" : "text-kumo-default"}`}
                  >
                    {event.method}
                  </span>
                  <span className="min-w-0 truncate">{event.summary}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="px-1 py-0.5 text-[9px] text-kumo-subtle">{emptyText}</p>
      )}
    </div>
  );
}
